import { Actions, createMapper, Getters, Module, Mutations } from 'vuex-smart-module'
import { showDefaultErrorMessage } from '@/utils/createMessage'
import { getToken, removeToken, setToken } from '@/utils/auth'
import { UserRepository } from '@/modules/session/repositories/User.repository'
import { TLoginData, TLoginResponse } from '@/modules/session/models/Auth.model'
import { AuthRepository } from '@/modules/session/repositories/Auth.repository'
import { getALivableRedirectPatch } from '@/router/helpers'
import router from '@/core/utils/router'
import { TRoleUser, TUser } from '@/modules/session/models/User.model'

const userRepository = new UserRepository()
const authRepository = new AuthRepository()

class UserState {
  token: string = getToken()
  refreshToken: string = ''
  userEmailData: string = ''
  user: TUser = {}
}

class UserGetters extends Getters<UserState> {
  getJwtToken() {
    const jwtToken = this.state.token.split(' ')[1]
    const tokenParts = jwtToken.split('.')

    return {
      headers: JSON.parse(atob(tokenParts[0])),
      payload: JSON.parse(atob(tokenParts[1])),
    }
  }

  getActualPark() {
    return this.state.user.parks?.find((e) => e.id === this.getters.getJwtToken().payload.park)
  }

  getUser() {
    return this.state.user
  }

  getUserParks() {
    return this.state.user.parks
  }

  getUserParkById() {
    return (id: string) => {
      return this.state.user.parks?.find((e) => e.id === id)?.name
    }
  }

  getIsUserSet() {
    return Object.keys(this.state.user).length !== 0
  }

  getUserRolesAccessCategories() {
    return this.state.user?.accessCategories?.map((x) => x.alias)
  }

  getUserRolesAccesses() {
    return this.state.user?.role?.reduce((acc: any, value) => {
      const data = value?.accesses?.map((x) => x?.alias) ?? []
      acc = [...acc, ...data]
      return acc
    }, [])
  }

  isUserFullAdmin() {
    const gelAllRolesAliases = this.state?.user?.role?.map((x: TRoleUser) => x?.alias) ?? []

    return gelAllRolesAliases.includes('admin') || gelAllRolesAliases.includes('park_owner')
  }
}

class UserMutations extends Mutations<UserState> {
  SET_USER(user: TUser) {
    this.state.user = user
  }

  SET_TOKENS(tokens: TLoginResponse) {
    this.state.token = `Bearer ${tokens.accessToken}`
    this.state.refreshToken = `Bearer ${tokens.refreshToken}`
  }

  SET_EMAIL(email: string) {
    this.state.userEmailData = email
  }

  SET_EMPTY_TOKEN() {
    this.state.token = ''
    this.state.refreshToken = ''
  }
}

class UserActions extends Actions<UserState, UserGetters, UserMutations, UserActions> {
  async fetchUser() {
    try {
      const response = await userRepository.getMe()

      this.commit('SET_USER', response)
    } catch (err) {
      showDefaultErrorMessage()
      removeToken()
    }
  }

  async fetchSwitchPark(parkId: string) {
    try {
      const response = await userRepository.switchPark(parkId)
      this.commit('SET_TOKENS', response)
      setToken(`Bearer ${response?.accessToken}`)

      window.location.reload()
    } catch (err) {
      showDefaultErrorMessage()
    }
  }

  async loginUser(userData: TLoginData) {
    try {
      const response = await authRepository.login(userData)

      this.commit('SET_TOKENS', response)
      this.commit('SET_EMAIL', userData.email)

      setToken(`Bearer ${response?.accessToken}`)

      await this.dispatch('fetchUser')

      await router.push({
        name: getALivableRedirectPatch(this.getters.getUserRolesAccessCategories()) || 'accounting',
      })
    } catch (err) {
      showDefaultErrorMessage()
    }
  }

  async logoutUser() {
    try {
      this.commit('SET_EMPTY_TOKEN')
      removeToken()
      await router.push({ path: '/login' })
      location.reload()
    } catch (err) {
      showDefaultErrorMessage()
    }
  }
}

export const userStore = new Module({
  namespaced: true,
  state: UserState,
  getters: UserGetters,
  mutations: UserMutations,
  actions: UserActions,
})

export const userMapper = createMapper(userStore)
